import { styled } from 'canoe';
import { nanoid } from 'nanoid';
import React, { ReactElement } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import { createVariantsSelector } from 'craft/components/SelectedToolbar';
import { BuiltInComponents } from 'craft/constants';
import { useNodeContext } from 'craft/hooks';
import { AtomicText } from 'craft/resolvers/AtomicText';
import { useForm, useFormSelectionManager } from 'craft/state';
import { CraftNode, Resolver, ToolboxComponentSections } from 'craft/types';

import { FormErrorMessage } from './FormErrorMessage';
import { FormStep } from './FormStep';
import { formInsertNodeTreesAtIndex } from './mutations';
import { FormVariant } from './toolbox/FormVariant';
import { useFormStepsSuccessNormalization } from './useFormStepsSuccessNormalization';

export const generateFormId = () => nanoid(6);

const FormContainer = styled.div``;

interface Props {
  children?: ReactElement | ReactElement[];
  formId: string;
  name?: string;
  submitRedirectUrl?: string;
  errorText?: string;
  variant?: string;
}

export const Form: Resolver = ({ children, errorText, variant = 'base' }: Props) => {
  const { id } = useNodeContext();
  const { index, shouldDisplayErrorState } = useForm(id);

  const formClassName = toCssSelector('form', {
    output: 'html',
    variant: variant ? { variant } : undefined,
  });

  useFormStepsSuccessNormalization(id);
  useFormSelectionManager(id);

  const variantClassName = `variant-base ${formClassName}`;
  return (
    <Node is={FormContainer} className={variantClassName} data-type="form">
      {/* @ts-ignore */}
      {children?.props?.children[index]}
      {shouldDisplayErrorState && errorText && (
        <FormErrorMessage data-atomictext-node data-nid={id}>
          <AtomicText propKey="errorText" childrenString={errorText} />
        </FormErrorMessage>
      )}
    </Node>
  );
};

Form.craft = {
  displayName: (node) => {
    if (node.data.nodes.length > 2) {
      return 'Multistep Form';
    }

    return 'Form';
  },
  name: BuiltInComponents.Form,
  isCanvas: true,
  rules: {
    canDrop(dropTarget, self, helpers): boolean {
      // We can't drop a form inside another form, no matter how nested
      const nodeApi = helpers(dropTarget.id);
      const ancestors = nodeApi.ancestors(true);

      if (dropTarget.data.name === BuiltInComponents.Form) {
        return false;
      }

      if (
        ancestors.find((ancestor) => nodeApi.nodes[ancestor].data.name === BuiltInComponents.Form)
      ) {
        return false;
      }

      return true;
    },
    canMoveIn: (incomingNodes: CraftNode[], self) =>
      incomingNodes.every(
        (node) => node.data.name === BuiltInComponents.FormStep && node.data.parent === self.id,
      ),
  },
};

Form.toolbox = {
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: FormVariant,
    },
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['flex', 'inline-block'],
    },
  },
};

Form.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      getConditions: (props) => ({
        name: props.variant?.value || 'base',
        variant: props.variant?.value,
      }),
      componentName: 'form',
      variantToProps: (variant) => ({
        variant: variant.conditions.variant,
      }),
    }),
  ],
};

Form.mutations = {
  insertNodeTreesAtIndex: formInsertNodeTreesAtIndex,
};

Form.flow = {
  stepResolver: FormStep,
};
